import RenderManager from "../rendering/render-manager";
import SlideManager from "../utils/slide-manager";
import * as Events from "../utils/events";

const labels = [{
    name: "north",
    glyph: '\u2b06',
    angle: [Math.PI / 3, 2 * Math.PI / 3]
}, {
    name: "west",
    glyph: '\u2b05',
    angle: [5 * Math.PI / 6, 7 * Math.PI / 6],
}, {
    name: "south",
    glyph: '\u2b07',
    angle: [4 * Math.PI / 3, 5 * Math.PI / 3]
}, {
    name: "north-east",
    glyph: '\u2b08',
    angle: [Math.PI / 6, Math.PI / 3]
}, {
    name: "north-west",
    glyph: '\u2b09',
    angle: [2 * Math.PI / 3, 5 * Math.PI / 6]
}, {
    name: "south-west",
    glyph: '\u2b0b',
    angle: [7 * Math.PI / 6, 4 * Math.PI / 3]
}, {
    name: "south-east",
    glyph: '\u2b0a',
    angle: [5 * Math.PI / 3, 11 * Math.PI, 6]
}];

const eastLabel = {
    name: "east",
    glyph: '\u27a1'
};

class Racetrack extends THREE.Object3D {
    constructor({
        track = "",
        actionUuid = "",
        trackLength = 100,
        trackRadius = 20,
        axisOffset = 0 } = {}) {
        super();

        this.type = "Racetrack";
        this.isRacetrack = true;
        this._needsInit = true;

        this._trackLength = trackLength;
        this._trackRadius = trackRadius;
        this._trackUuid = track;
        this._actionUuid = actionUuid;
        this._axisOffset = axisOffset;

        this._track = null;
        this._redCar = null;
        this._action = null;

        Events.listen("slidechange", this._onSlideChange.bind(this));

        if (!Q3.editMode) {
            this._layer = document.createElement("div");
            this._arrow = document.createElement("div");
            this._compass = document.createElement("div");
            this._container = document.createElement("container");

            this._layer.classList.add("racetrack");
            this._container.classList.add("container");
            this._compass.classList.add("compass")
            this._arrow.classList.add("arrow");

            this._layer.appendChild(this._container);
            this._container.appendChild(this._compass);
            this._container.appendChild(this._arrow);
            RenderManager.overlay2d.appendChild(this._layer);
        }

        this.tryFindingDefaultTrack();
    }

    get actionUuid() { return this._actionUuid; }
    set actionUuid(v) { this._actionUuid = v; }

    get axisOffset() { return this._axisOffset; }
    set axisOffset(v) { this._axisOffset = v; }

    get needsInit() { return this._needsInit; }

    _hasActiveAction() { return Q3.mixer.getObjectAction(this._track.uuid).activeAction }

    _findAction() {
        const actions = Q3.mixer.getObjectAction(this._track.uuid).actions;

        return actions[this._actionUuid];
    }

    _onSlideChange() {
        if (this._needsInit || Q3.editMode) return;

        this._action = this._findAction();
        this.reset();

        if (this._action) {
            this._startTime = Q3.mixer.time - this._action.time / this._action.timeScale;
        }
    }

    reset() {
        if (Q3.editMode) return;

        const reset = () => {
            if (this._action) {
                this._action.time = 0;
            }

            Events.fire("racetrack_update", {
                racetrack_distance: "0m",
                racetrack_displacement: "0m north",
            });
        };

        reset();
    }

    tryFindingDefaultTrack() {
        if (this._track || this._trackUuid === "") return;

        const defaultTrack = RenderManager.activeScene.getObjectByName("Racetrackv2")

        if (defaultTrack) {
            this._track = defaultTrack;
            this._trackUuid = this._track.uuid;

            if (!this._action && this._actionUuid !== "") {
                const actions = Q3.mixer.getObjectAction(this._track.uuid).actions;

                if (actions) {
                    const all = Object.keys(actions).find(key => actions[key].getClip().name === "All");

                    if (all) this._actionUuid = all;
                }
            }
        }
    }

    get asset() { return this._track; }

    get trackLength() { return this._trackLength; }
    set trackLength(v) { this._trackLength = v; }

    get trackRadius() { return this._trackRadius; }
    set trackRadius(v) { this._trackRadius = v; }

    get trackUuid() { return this._trackUuid; }
    set trackUuid(v) {
        this._track = Q3.assets.objects[v];
        this._trackUuid = v;
    }

    init() {
        this._track = Q3.assets.objects[this._trackUuid];

        if (!this._track) return;

        this._needsInit = false;

        this._redCar = this._track.getObjectByName("Car_red");
        this._startingLine = this._track.getObjectByName("Start_tracking");

        this._action = this._findAction();
        if (this._action) {
            this._startTime = Q3.mixer.time - this._action.time / this._action.timeScale;
        }

        this.reset();
    }

    update() {
        if (this._needsInit || Q3.editMode || !this._track || !this._redCar) return;

        this._action = this._findAction();
        if (!this._action) {
            if (this._needsInit) this.init();
            this.reset();
            this._layer.style.display = "none";
            return;
        }

        const slide = SlideManager.currentSlide;

        const track = slide.animation[this.trackUuid];

        if (slide.animation[this.uuid].visible === false || track && track.visible === false) {
            this._layer.style.display = "none";
            return;
        }

        const clip = this._action.getClip();

        const calcDistance = () => {
            if (this._action.paused || !this._hasActiveAction()) {
                return (this._action.time / clip.duration) * this._trackLength;
            } else {
                const dt = Q3.mixer.time - this._startTime;
                const loops = (dt * this._action.timeScale) / clip.duration;

                return loops * this._trackLength;
            }
        }

        const distance = calcDistance();

        const p1 = new THREE.Vector3()
            .copy(this._redCar.position)

        const p2 = new THREE.Vector3()
            .copy(this._startingLine.position)

        const displacementVector = new THREE.Vector3()
            // .subVectors(this._redCar.position, new THREE.Vector3())
            .subVectors(p1, p2)
            .multiply(this._track.parent.scale)
            .setY(0);

        const displacement = displacementVector.length() * this._trackRadius;

        const eye = new THREE.Vector3()
            .copy(this._startingLine.position)
            .applyMatrix4(this._track.matrixWorld)
            .setY(0);

        const dir = new THREE.Vector3()
            .copy(this._redCar.position)
            .applyMatrix4(this._track.matrixWorld)
            .setY(0);

        const a = new THREE.Vector3()
            .copy(eye)
            .project(RenderManager.activeCamera);
        const b = new THREE.Vector3()
            .copy(dir)
            .project(RenderManager.activeCamera);

        const c = new THREE.Vector3().subVectors(b, a).normalize();

        function getAngle(x1, y1, x2, y2) {
            const dot = x1 * x2 + y1 * y2;
            const det = x1 * y2 - y1 * x2;

            let angle = Math.atan2(det, dot);
            if (angle < 0) angle = Math.PI * 2 - Math.abs(angle);

            return angle;
        }

        const angle = getAngle(c.x, c.y, 0, 1);

        this._arrow.style.transform = `rotate(${angle}rad)`;

        // const bestLabel = labels.find(label => angle <= label.angle[1] && angle >= label.angle[0]) || eastLabel;
        // const label = bestLabel.glyph;

        Events.fire("racetrack_update", {
            racetrack_distance: `${Math.round(distance)}m`,
            racetrack_displacement: `${Math.round(displacement)}m`,
        });

        this._layer.style.display = "block";

        this._layer.style.top = Q3.letterbox.style.top;
    }

}

export default Racetrack;
export { Racetrack };